import React, { useEffect, useRef, useState } from "react";
import { SelectInstance, SingleValue } from "react-select";

import CategoryApi from "../api/categoryApi";
import PostApi from "../api/postApi";
import CountrySelector from "../components/CountrySelector";
import PostListItem from "../components/PostListItem";
import StateRegionSelector from "../components/StateRegionSelector";
import CurrencyTagsSelector from "../components/CurrencyTagsSelector";
import { ICategory, IPost, IProduct, ITag } from "../config/interface";
import UtilService from "../services/UtilService";
import ProductSelector from "../components/ProductSelector";

export interface IChildCat {
  [key: string]: ICategory[];
}

const CountryPosts = () => {
  const [tagsData, setTagsData] = useState<ITag[]>([]);
  const [productsData, setProductsData] = useState<IProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedCountriesProducts, setSelectedCountriesProduct] = useState<IProduct[]>([]);

  const [countriesWithoutCurrency, setCountriesWithoutCurrency] = useState<number[]>([]);
  const [postsData, setPostsData] = useState<IPost[]>([]);

  const [showCatOrCurrencySelector, setShowCatOrCurrencySelector] = useState<boolean>(false);
  const [showProductSelector, setShowProductSelector] = useState<boolean>(false);
  const [selectedParentCat, setSelectedParentCat] = useState<ICategory | null>(null);
  const [selectedCatOrCurrency, setSelectedCatOrCurrency] = useState<ICategory | ITag | null>(null);
  const [selectedCatTagType, setSelectedCatTagType] = useState<"category" | "tag" | null>(null);

  const [parentData, setParentData] = useState<ICategory[]>([]);
  const [childrenData, setChildrenData] = useState<IChildCat | null>(null);
  const [fetchData, setFetchData] = useState<boolean>(false);
  const [isPostsFetching, setIsPostsFetching] = useState<boolean>(false);
  const [patchUrlParam, setPatchUrlParam] = useState<boolean>(false);

  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(new Date().getTime());

  let stateRegionSelectorRef = useRef<SelectInstance<ITag> | null>(null);
  let tagSelectorRef = useRef<SelectInstance<ITag> | null>(null);
  let productSelectorRef = useRef<SelectInstance<IProduct> | null>(null);

  const initCategory = async (): Promise<ICategory[]> => {
    return new Promise(async (resolve) => {
      try {
        const api = CategoryApi.getInstance();
        let currentIndex = 0;
        let allCategories: ICategory[] = [];
        let response: any = null;

        do {
          currentIndex++;
          response = await api.getCategory(currentIndex);
          const { data }: { data: ICategory[] } = response;
          allCategories = currentIndex === 1 ? data : [...allCategories, ...data];
        } while (
          currentIndex !== Number(response.headers["x-wp-totalpages"]) &&
          Number(response.headers["x-wp-totalpages"]) !== 0
        );

        resolve(allCategories);
      } catch (e) {
        resolve([]);
      }
    });
  };

  const initTags = async () => {
    try {
      const api = CategoryApi.getInstance();
      const tags = await api.getDefaultTags();
      return tags.data;
    } catch (error) {
      UtilService.showMessage(UtilService.getErrorMessage(error));
      return [];
    }
  };

  const initExcludedCat = async () => {
    try {
      const api = CategoryApi.getInstance();
      const exc = await api.getExcludedAndWithoutCurrencyCategories();
      return exc.data;
    } catch (error) {
      UtilService.showMessage(UtilService.getErrorMessage(error));
      return [];
    }
  };

  const initGetAllPosts = async (): Promise<IPost[]> => {
    return new Promise(async (resolve) => {
      try {
        const api = PostApi.getInstance();
        let currentIndex = 0;
        let allPosts: IPost[] = [];
        let response: any = null;

        do {
          currentIndex++;
          if (selectedParentCat?.id) {
            if (countriesWithoutCurrency.includes(selectedParentCat.id)) {
              response = await api.getPostsWithoutCurrency(selectedParentCat.id);
            } else {
              if (selectedCatOrCurrency?.id && selectedCatTagType) {
                // if product exists for that country
                if (selectedProduct?.id!==-1) {
                  response = await api.getPosts(
                    selectedParentCat.id,
                    selectedCatOrCurrency?.id,
                    selectedCatTagType,
                    selectedProduct?.id
                  );
                } else {
                  response = await api.getPosts(selectedParentCat.id, selectedCatOrCurrency?.id, selectedCatTagType);
                }
              }
            }
          }

          const { data }: { data: IPost[] } = response;
          allPosts = currentIndex === 1 ? data : [...allPosts, ...data];
        } while (
          currentIndex !== Number(response.headers["x-wp-totalpages"]) &&
          Number(response.headers["x-wp-totalpages"]) !== 0
        );

        resolve(allPosts);
      } catch (e) {
        resolve([]);
      }
    });
  };


  const initProducts = async () => {
    try {
      const api = CategoryApi.getInstance();
      const products = await api.getProducts();
      return products.data;
    } catch (error) {
      UtilService.showMessage(UtilService.getErrorMessage(error));
      return [];
    }
  };


  useEffect(() => {
    if (!fetchData) {
      return;
    }
    (async () => {
      try {
        const [categories, tags, excluded, products] = await Promise.all([
          initCategory(),
          initTags(),
          initExcludedCat(),
          initProducts(),
        ]);

        setTagsData(tags);
        setCountriesWithoutCurrency(() => excluded.bfdl_parent_only_categories);
        setProductsData(products);

        // divide the whole data (categories) in parent (value==0) and children array
        // from parent array remove the excluded countries

        const p: ICategory[] = [];
        const c: IChildCat = {};
        categories.forEach((cat) => {
          if (!excluded.bfdl_excluded_categories.includes(cat.id)) {
            if (cat.parent === 0) {
              p.push(cat);
            } else {
              c[cat.parent] ? c[cat.parent].push(cat) : (c[cat.parent] = [cat]);
            }
          }
        });

        setParentData(p);
        setChildrenData(c);
      } catch (error) {
      } finally {
        setFetchData(false);
        setPatchUrlParam(true);
      }
    })();
  }, [fetchData]);

  useEffect(() => {
    setFetchData(true);
  }, []);


  useEffect(() => {
    getPosts();
  }, [currentTimeStamp]);

  useEffect(() => {
    if (patchUrlParam) {
      // get the url params and
      const queryParameters = new URLSearchParams(window.location.search);
      // set the param value to country selector
      if (queryParameters.get("country")) {
        handleOnSelectCountry(parentData.find((c) => c.slug === queryParameters.get("country")));
      }
      setPatchUrlParam(false);
    }
  }, [patchUrlParam]);

  const getPosts = async () => {
    try {
      setIsPostsFetching(true);
      const posts = await initGetAllPosts();
      setPostsData(() => posts);
    } catch (error) {
      UtilService.showMessage(UtilService.getErrorMessage(error));
    } finally {
      setIsPostsFetching(false);
    }
  };

  const setStateRegionSelectorRef = (ref: any) => {
    stateRegionSelectorRef = ref;
  };
  const setProductSelectorRef = (ref: any) => {
    productSelectorRef = ref;
  };

  const setTagSelectorRef = (ref: any) => {
    tagSelectorRef = ref;
  };
  const handleOnSelectCountry = (selectedCategory: SingleValue<ICategory> | undefined) => {
    if (selectedCategory?.id) {
      setSelectedCatOrCurrency(null);
      setSelectedCatTagType(null);
      setSelectedProduct(null);
      setPostsData(() => []);

      tagSelectorRef?.current?.clearValue();
      stateRegionSelectorRef?.current?.clearValue();
      productSelectorRef?.current?.clearValue();

      setSelectedParentCat(selectedCategory);

      if (countriesWithoutCurrency.includes(selectedCategory.id)) {
        setShowCatOrCurrencySelector(() => false);
        setCurrentTimeStamp(new Date().getTime());
      } else {
        setShowCatOrCurrencySelector(() => true);
      }

      const productsBasedOnCountry = productsData.filter(
        (product: IProduct) => Number(product.acf.country) === selectedCategory?.id
      );
      setShowProductSelector(productsBasedOnCountry.length !== 0);

      productsBasedOnCountry.length !== 0
        ? setSelectedCountriesProduct([
            {
              id: -1,
              name: "All",
              slug: "all",
              acf: {
                country: "-1",
              },
            },
            ...productsBasedOnCountry,
          ])
        : setSelectedCountriesProduct([]);
    }
  };

  return (
    <section className="bf-library-search">
      {/* <!-- Filter Block Starts here --> */}
      <div className="container-fluid container-bf-library-search-form section-border-left section-border-right section-border-bottom">
        <div className="row px-md-4">
          <CountrySelector
            data={parentData}
            isDataFetching={fetchData}
            selectedValue={selectedParentCat}
            onSelectCategory={(selectedCategory) => {
              handleOnSelectCountry(selectedCategory);
            }}
          />

          {selectedParentCat && showCatOrCurrencySelector && (
            <>
              {childrenData && childrenData[selectedParentCat.id] ? (
                <StateRegionSelector
                  data={childrenData[selectedParentCat.id]}
                  selectedValue={selectedCatOrCurrency}
                  onSelectCategory={(selectedCat) => {
                    if (selectedCat?.id && selectedParentCat) {
                      setSelectedCatOrCurrency(selectedCat);
                      setSelectedCatTagType("category");
                      setPostsData(() => []);

                      if (selectedCat && selectedCountriesProducts.length && selectedProduct) {
                        setCurrentTimeStamp(new Date().getTime());
                      } else if (selectedCat && !selectedCountriesProducts.length) {
                        setCurrentTimeStamp(new Date().getTime());
                      }
                    }
                  }}
                  setSelectorRef={setStateRegionSelectorRef}
                />
              ) : (
                <CurrencyTagsSelector
                  data={tagsData}
                  selectedValue={selectedCatOrCurrency}
                  onSelectCategory={(selectedCat) => {
                    if (selectedCat?.id && selectedParentCat) {
                      setSelectedCatOrCurrency(selectedCat);
                      setSelectedCatTagType("tag");
                      setPostsData(() => []);
                      if (selectedCat && selectedCountriesProducts.length && selectedProduct) {
                        setCurrentTimeStamp(new Date().getTime());
                      } else if (selectedCat && !selectedCountriesProducts.length) {
                        setCurrentTimeStamp(new Date().getTime());
                      }
                    }
                  }}
                  setSelectorRef={setTagSelectorRef}
                />
              )}
            </>
          )}
          {selectedParentCat && showProductSelector ? (
            <ProductSelector
              data={selectedCountriesProducts}
              selectedValue={selectedProduct}
              onSelectProduct={(_selectedProduct) => {
                if (_selectedProduct?.id && selectedParentCat) {
                  setSelectedProduct(_selectedProduct);
                  setPostsData(() => []);
                  if (selectedCatTagType) {
                    setCurrentTimeStamp(new Date().getTime());
                  }
                }
              }}
              setSelectorRef={setProductSelectorRef}
            />
          ) : null}
        </div>
      </div>
      {/* <!-- Filter Block Ends here --> */}

      {/* <!-- Results Block Starts here --> */}
      <div className="container-fluid container-bf-library-search-results section-border-left section-border-right section-border-bottom">
        {/* <!-- State 1 --> */}
        <div className="row p-4">
          <div className="col">
            {!selectedParentCat && <p className="fs-5 text-muted">Please select a Country.</p>}

            {selectedParentCat && showCatOrCurrencySelector && (
              <>
                {childrenData && childrenData[selectedParentCat.id] && !selectedCatOrCurrency ? (
                  <p className="fs-5 text-muted">Please select a State/Region.</p>
                ) : (
                  <>{!selectedCatOrCurrency && <p className="fs-5 text-muted">Please select a Currency.</p>}</>
                )}
              </>
            )}
            {selectedParentCat && showProductSelector && (
              <>{!selectedProduct ? <p className="fs-5 text-muted">Please select a Product.</p> : null} </>
            )}

            {postsData?.length > 0 && (
              <p className="fs-5 text-uppercase bf-library-search-count">{postsData.length} result(s)</p>
            )}
          </div>
        </div>

        {/* <!-- Results List Starts here --> */}
        <div className="row px-4 bf-library-search-result-wrapper">
          {isPostsFetching ? (
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <div className="col-12">
              <>
                {postsData.length > 0 ? (
                  <>
                    {postsData.map((post) => (
                      <div key={post.id}>
                        <PostListItem post={post} />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {selectedParentCat && selectedCatOrCurrency && (
                      <>
                        {selectedCountriesProducts.length && selectedProduct ? (
                          <p className="fs-5 text-muted">No document found.</p>
                        ) : !selectedCountriesProducts.length ? (
                          <p className="fs-5 text-muted">No document found.</p>
                        ) : null}
                      </>
                    )}
                  </>
                )}
              </>
            </div>
          )}
        </div>
        {/* <!-- Results List Ends here --> */}
      </div>
      {/* <!-- Results Block Ends here --> */}
    </section>
  );
};

export default CountryPosts;
